import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Trash2, Pencil, Plus } from "lucide-react";
import { api, fmtDate, errMsg } from "@/lib/api";
import { computeLayout } from "@/lib/layout";
import { StampSvg } from "@/components/StampSvg";
import { freeProduct, sizeLabel } from "@/pages/admin/AdminStampCreator";

function Thumb({ s }) {
  const size = { shape: s.shape, w: s.width_mm, h: s.height_mm };
  const layout = computeLayout(freeProduct(size), s.design);
  return <StampSvg layout={layout} color="#111827" scale={Math.min(260 / s.width_mm, 95 / s.height_mm)} />;
}

export function AdminSavedStamps() {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/saved-stamps", { params: { q } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q]);
  const del = async (s) => {
    if (!window.confirm(`Eliminare il timbro "${s.name}"?`)) return;
    try { await api.delete(`/admin/saved-stamps/${s.id}`); setList((l) => l.filter((x) => x.id !== s.id)); toast.success("Timbro eliminato"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const open = (s) => navigate(`/admin/crea-timbro?id=${s.id}`);
  return (
    <div className="space-y-4" data-testid="admin-saved-stamps">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-extrabold uppercase">Timbri salvati</h1>
          <p className="text-sm text-slate-500">Visibili solo all'amministratore. Cerca per nome e apri il timbro nell'editor.</p>
        </div>
        <Link to="/admin/crea-timbro" data-testid="saved-new-btn" className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-bold flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo timbro</Link>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <input data-testid="saved-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome…"
          className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70" />
      </div>
      {!list ? <p className="text-slate-500 text-sm">Caricamento…</p> : !list.length ? (
        <p className="text-slate-500 text-sm rounded-xl border bg-white p-8 text-center" data-testid="saved-empty">{q ? "Nessun timbro con questo nome." : "Nessun timbro salvato. Creane uno da Crea timbro."}</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {list.map((s) => (
            <div key={s.id} className="rounded-xl border bg-white overflow-hidden flex flex-col" data-testid={`saved-stamp-${s.id}`}>
              <button onClick={() => open(s)} className="ink-grid h-28 flex items-center justify-center" data-testid={`saved-thumb-${s.id}`}><Thumb s={s} /></button>
              <div className="p-3 flex items-center gap-2 border-t">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate" data-testid={`saved-name-${s.id}`}>{s.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{sizeLabel({ shape: s.shape, w: s.width_mm, h: s.height_mm })} · {fmtDate(s.created_at)}</div>
                </div>
                <button data-testid={`saved-open-${s.id}`} onClick={() => open(s)} className="press h-9 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Apri</button>
                <button data-testid={`saved-delete-${s.id}`} onClick={() => del(s)} title="Elimina" className="press w-9 h-9 rounded-md border border-red-200 bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
